<template>
  <div class="bg-grey-100" :style="playerColor">
    <v-wait for="videos">
      <half-circle-spinner
        slot="waiting"
        style="display: block; margin: auto auto"
        :animation-duration="1000"
        :size="60"
        color="#3d4852"
      />
      <div
        class="py-10 min-h-screen flex flex-col items-center w-full rounded-none"
      >
        <loading
          :active.sync="isLoading"
          :is-full-page="fullPageLoader"
          :color="loaderColor"
        ></loading>

        <div class="video-internal-wrapper-embed" v-if="vid && vid.is_published">
          <video-by-link
            v-if="fetchedVideo"
            ref="videoPlay"
            :key="videoPlayerComponentKey"
            :options="videoOptions"
            class="video-js vjs-big-play-centered"
            :class="vjsClass"
            :vid="vid"
          ></video-by-link>

          <div
            class="flex flex-wrap px-2 md:py-4 md:px-8 justify-between w-full h-full bg-white rounded-b items-center break-all"
          >
            <div class="flex flex-col w-3/4 py-2">
              <span
                class="font-bold text-lg md:text-xl lg:text-2xl 2xl:text-3xl text-grey-900"
                >{{ vid.name }}</span
              >
              <span class="text-xs md:text-sm text-grey-600"
                >{{ $t('frontend_posted_by') }}
                {{ vid.user_name || 'groovevideo user' }}</span
              >
            </div>
            <div v-if="allow_download" class="py-2">
              <gd-button
                primary
                class="h-10 w-32 hidden md:block uppercase video-download-btn"
                @click="downloadVideo"
                >{{ $t('frontend_video_download') }}</gd-button
              >
              <gd-button
                primary
                class="block md:hidden py-4 px-4"
                size="icon"
                @click="downloadVideo"
                ><em class="fas fa-cloud-download-alt"></em
              ></gd-button>
            </div>
          </div>
        </div>
        <div v-else-if="!isPublished" class="text-grey-900 text-base">
          {{ $t('frontend_groovevideo_links_video_not_published') }}
        </div>
      </div>
    </v-wait>
  </div>
</template>

<script type="text/javascript">
import 'vue-loading-overlay/dist/vue-loading.css';

import VideoByLink from '@groovepages/gd-ui-kit/components/video-player/VideoByLink';
import Loading from 'vue-loading-overlay';
import themeConfig from '@/theme/themeConfig';
import Video from '@/models/Video';

require('videojs-youtube');

export default {
  components: {
    VideoByLink,
    Loading,
  },
  layout: 'basic',
  data() {
    return {
      fetchedVideo: 0,
      playerColor: {},
      plink: '',
      videoPlayerComponentKey: null,
      embedded_video: {},
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      text_overlay: '',
      video_end: false,
      vid: null,
      videoOptions: {
        showControls: 1,
        controls: true,
        controlBar: {
          playToggle: true,
          captionsButton: false,
          chaptersButton: false,
          subtitlesButton: false,
          remainingTimeDisplay: true,
          progressControl: {
            seekBar: true,
          },
          fullscreenToggle: false,
          playbackRateMenuButton: false,
          pictureInPictureToggle: true,
          volumePanel: true,
          muteToggle: false,
        },
        autoplay: true,
        muted: false,
        poster: '',
        sources: [
          {
            src: '',
            type: 'video/mp4',
          },
        ],
        loop: false,
        userActions: {
          doubleClick: true,
        },
      },
      enable_control: 0,
      enable_mute: 0,
      enable_autoplay: 0,
      allow_download: 0,
      download_link: null,
      isPublished: true
    };
  },
  computed: {
    vjsClass() {
      if(this.vid.settings.theme == null || this.vid.settings.theme == " ") {
        return 'vjs-theme-default';
      } if(this.vid.settings.theme != " ") {
        return this.vid.settings.theme;
      } 
    }
  },
  async created() {
    this.$wait.start('videos');
    this.vid = await this.fetchVideo();

    if (!this.vid) {
      this.$wait.end('videos');
      return;
    }

    // Set player colors
    this.playerColor =
      typeof this.vid.settings.color !== 'undefined' &&
      typeof this.vid.settings.color.primary !== 'undefined' &&
      typeof this.vid.settings.color.secondary !== 'undefined'
        ? {
            '--player-primary-color': this.vid.settings.color.primary,
            '--player-secondary-color': this.vid.settings.color.secondary,
          }
        : {
            '--player-primary-color': 'rgba(253, 100, 129, 1)',
            '--player-secondary-color': 'rgba(255,255,255, 1)',
          };

    this.enable_control = this.vid.settings.control;
    this.enable_mute = this.vid.settings.muted;

    this.setHostedPageSettings();

    if (!this.vid.settings.control) {
      this.videoOptions.showControls = 0;
      this.videoOptions.controlBar.playToggle = false;
      this.videoOptions.controlBar.remainingTimeDisplay = false;
      this.videoOptions.controlBar.progressControl.seekBar = false;
      this.videoOptions.controlBar.fullscreenToggle = false;
      this.videoOptions.controlBar.pictureInPictureToggle = false;
      this.videoOptions.controlBar.volumePanel = false;
      this.videoOptions.controlBar.muteToggle = false;
    }

    this.videoOptions.muted = this.vid.settings.muted;
    this.videoOptions.poster = this.vid.settings.splashImg;

    this.fetchedVideo = 1;
    this.$wait.end('videos');
  },
  methods: {
    async fetchVideo() {
      const { permalink } = this.$route.params;
      const videoId = this.$route.params.id;
      
      try {
        return await Video.params({
          permalink,
          id: videoId,
        })
          .custom('fetch-with-hosted-permalink')
          .$first();

      } catch (err) {
        const { data } = err.response;
        if(data && data.message && data.message === 'video_not_published') {
          this.isPublished = false;
        }
        return null;
      }
    },

    setHostedPageSettings() {
      if (this.vid.hosted_page_settings != null) {
        this.enable_autoplay = this.vid.hosted_page_settings.autoplay;
        if (this.vid.video_source_type === 1 && this.vid.download_url) {
          this.allow_download = this.vid.hosted_page_settings.allow_download;
          this.download_link =
            process.env.NODE_ENV === 'development'
              ? this.vid.download_url
              : this.vid.download_url.replace('http://', 'https://');
        }
      }

      // Autoplay settings
      if (this.enable_autoplay) {
        this.videoOptions.autoplay = 'muted';
      } else {
        this.videoOptions.autoplay = false;
      }

      this.playerColor['--dimension'] = this.vid.dimension_percentage ? (100/this.vid.dimension_percentage) : 1.78;
    },

    async downloadVideo() {
      window.open(this.download_link, '_self');
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style>
.video-download-btn {
  background-color: var(--player-primary-color) !important;
  border-color: var(--player-primary-color) !important;
}
</style>
<style scoped>
.video-internal-wrapper-embed {
  width: 80%;
  overflow: hidden;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  max-width: calc(var(--dimension) * 80vh);
  min-width: 340px;
}

::v-deep .videojs-event-tracking-player-dimensions {
  width: 0px !important;
  height: 0px !important;
}

.img {
  width: 50px !important;
  position: relative;
  right: -300px;
  opacity: 0.7;
  cursor: pointer;
}

.vjs-big-play-centered {
  border-bottom-left-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
}

.vjs-control-bar,
.optin-form-cta-contaner .cta-optin-form-submit-button,
.optin-form-cta-contaner .cta-optin-form-submit-button:hover,
.optin-form-cta-contaner .cta-optin-form-submit-button:active,
.optin-form-cta-contaner .cta-optin-form-submit-button:focus,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:hover,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:active,
.video-download-btn {
  background-color: var(--player-primary-color) !important;
}
.cta-skip-button:hover,
.banner-skip:hover {
  color: var(--player-primary-color) !important;
}
.video-js {
  color: var(--player-secondary-color) !important;
  max-height: 80vh !important;
  height: auto !important;
}
</style>

